import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithAuth } from "./baseQuery";

const baseQueryWithProfiles = (args, api, extraOptions) => {
    if (typeof args === 'string') {
        args = { url: args };
    }
    args.url = `/profiles${args.url ? `/${args.url}` : ''}`;
    return baseQueryWithAuth(args, api, extraOptions);
};

export const companyProfileApi = createApi({
    reducerPath: "companyProfileApi",
    baseQuery: baseQueryWithProfiles,
    tagTypes: ["CompanyProfile"],
    endpoints: (builder) => ({
        getCompanyProfiles: builder.query({
            async queryFn({ pageSize = 200, nextToken = undefined, page = 1 } = {},
                queryApi,
                extraOptions,
                baseQuery
            ) {
                const result = await baseQuery({
                    url: "",
                    params: { pageSize, ...(nextToken ? { nextToken } : {}), page },
                });
                return result;
            },
            providesTags: ["CompanyProfile"],
            keepUnusedDataFor: 0,
        }),
        getUserProfiles: builder.query({
            query: (searchQuery) => ({
                url: "",
                params: searchQuery ? { search: searchQuery } : {},
            }),
            providesTags: ["CompanyProfile"],
        }),
        getAllCompanyProfiles: builder.query({
            async queryFn({ searchQuery = "", createdFrom = "", createdTo = "", status = "", page = 1, limit = 100 } = {}, queryApi, extraOptions, baseQuery) {
                const result = await baseQuery({
                    url: "all",
                    params: {
                        ...(searchQuery && { search: searchQuery }),
                        ...(createdFrom && { createdFrom }),
                        ...(createdTo && { createdTo }),
                        ...(status && { status }),
                        page,
                        limit
                    },
                });
                return result;
            },
            providesTags: (result, error, arg) => {
                const { searchQuery = "", createdFrom = "", createdTo = "", status = "", page = 1 } = arg || {};
                return searchQuery || createdFrom || createdTo || status || page > 1
                    ? [{ type: "CompanyProfile", id: `SEARCH-${searchQuery}-${createdFrom}-${createdTo}-${status}-${page}` }]
                    : ["CompanyProfile"];
            },
            refetchOnMountOrArgChange: true,
        }),
        getCompanyProfileById: builder.query({
            query: (id) => `${id}`,
            providesTags: ["CompanyProfile"],
        }),
        getComplianceStatusBulk: builder.query({
            query: () => "compliance-status",
            providesTags: ["CompanyProfile"],
        }),
        getComplianceStatusById: builder.query({
            query: (id) => `${id}/compliance-status`,
            providesTags: (result, error, id) => [{ type: "CompanyProfile", id }, "CompanyProfile"],
        }),
        createCompanyProfile: builder.mutation({
            query: (data) => ({
                url: "",
                method: "POST",
                body: data,
            }),
            invalidatesTags: ["CompanyProfile"],
        }),
        updateCompanyProfile: builder.mutation({
            async queryFn({ id, formData, token },
                queryApi,
                extraOptions,
                baseQuery
            ) {
                const result = await baseQuery({
                    url: token ? `${id}/${token}` : `${id}`,
                    method: "PUT",
                    body: formData,
                });
                return result;
            },
            invalidatesTags: ["CompanyProfile"],
        }),
        updateCompanyProfileStatus: builder.mutation({
            query: ({ id, status, reason }) => ({
                url: `${id}/status`,
                method: "PUT",
                body: { status, reason },
            }),
            invalidatesTags: ["CompanyProfile"],
        }),
        deleteCompanyProfile: builder.mutation({
            query: (id) => ({
                url: `${id}`,
                method: "DELETE",
            }),
            invalidatesTags: ["CompanyProfile"],
        }),
    }),
});

export const {
    useGetCompanyProfilesQuery,
    useGetAllCompanyProfilesQuery,
    useLazyGetAllCompanyProfilesQuery,
    useGetCompanyProfileByIdQuery,
    useGetComplianceStatusBulkQuery,
    useGetComplianceStatusByIdQuery,
    useGetUserProfilesQuery,
    useCreateCompanyProfileMutation,
    useUpdateCompanyProfileMutation,
    useUpdateCompanyProfileStatusMutation,
    useDeleteCompanyProfileMutation,
} = companyProfileApi;
